/** @jsx jsx */
import { useCallback, useEffect, useState, type PropsWithChildren } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { useIsSiteAdmin } from '@confluence/current-user';
import { ConfluenceEdition } from '@confluence/change-edition';
import { LoadableConfluenceAiUpsellModal } from '@confluence/experiment-ai-upsell-modal/entry-points/LoadableAiUpsellModal';

const aiUpsellButtonContainerStyles = css({
	borderRadius: `calc(${token('border.radius', '3px')} + 1px)`,
	background: `conic-gradient(
    from 270deg,
    #0065FF 0%,
    #0469FF 20%,
    #BF63F3 50%,
    #FFA900 56%,
    #0065FF 100%
  )`,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	padding: '1px',
	display: 'inline-flex',
});

const aiUpsellButtonInnerStyles = css({
	backgroundColor: token('elevation.surface'),
	borderRadius: token('border.radius', '3px'),
});

/**
 * boolean : When the site meets the requirements (true for control and false for variation)
 * undefined: When the site is not enrolled, site/user does not meet the requirements
 */
type ExperimentValue = boolean | undefined;

export type UseAIUpsellInEditorFreeResult = {
	/**
	 * If the site is loading the admin check
	 */
	isLoading: boolean;
	/**
	 * The site/user meets the requirements
	 */
	isEnrolledInExperiment: boolean;
	/**
	 * After the admin check is done, this says if you are in the experiment or not
	 * (true for control and false for variation)
	 */
	isExperimentEnabled: boolean;
	/**
	 * Fire the experiment exposure
	 */
	fireExperimentExposure: () => void;
	triggerUpsellFlow: () => void;
	UpsellModal: React.FC;
};

const computeExperimentValue = (
	isAdminCheckLoading: boolean,
	isSiteAdmin: boolean,
	statsigExperimentValue: boolean,
): ExperimentValue => {
	// Permissions are being checked
	if (isAdminCheckLoading || !isSiteAdmin) {
		return undefined;
	}
	return isSiteAdmin && statsigExperimentValue;
};

/**
 * Duplicated from app-navigation/src/AppSwitcherContent.tsx
 *
 * This prevents an event from being fired again due to rerenders
 * We could export this for use within the package if needed
 */
const { fireEventOnce } = (() => {
	let hasFired = false;

	return {
		fireEventOnce: (fireFunction: () => void) => {
			if (!hasFired) {
				fireFunction();
				hasFired = true;
			}
		},
	};
})();

export const useAIUpsellInEditorFree = (
	edition: ConfluenceEdition | null,
	aiOptInStatus: string,
): UseAIUpsellInEditorFreeResult => {
	const { isSiteAdmin, isAdminCheckLoading } = useIsSiteAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [showUpsellModal, setShowUpsellModal] = useState(false);

	const statsigExperimentValue = FeatureGates.getExperimentValue<ExperimentValue>(
		'confluence_free_ai_feature_gate',
		'experimentEnabled',
		undefined,
		{
			fireExperimentExposure: false,
		},
	);

	const siteMeetsRequirements =
		edition === ConfluenceEdition.FREE &&
		typeof statsigExperimentValue === 'boolean' &&
		// optInStatus can be "enabled on free sites"
		// optInStatus disabled means they can't use AI
		aiOptInStatus == 'disabled-opt-in';

	// If site doesn't meet requirements, we don't need to wait for the admin check
	const [isLoading, setIsLoading] = useState<boolean>(
		siteMeetsRequirements ? isAdminCheckLoading : false,
	);

	// If siteAdmin information is ready, we can set the experiment value as the default value, to avoid recalculations when the query is ready
	const [experimentValue, setExperimentValue] = useState<ExperimentValue>(
		siteMeetsRequirements
			? computeExperimentValue(isAdminCheckLoading, isSiteAdmin || false, statsigExperimentValue)
			: undefined,
	);

	const isEnrolledInExperiment = typeof experimentValue === 'boolean';
	const isExperimentEnabled = !isLoading && isEnrolledInExperiment && experimentValue;

	// This useEffect will be to listed the changes on the isAdminCheckLoading and isSiteAdmin
	useEffect(() => {
		if (siteMeetsRequirements) {
			const experimentValue = computeExperimentValue(
				isAdminCheckLoading,
				isSiteAdmin || false,
				statsigExperimentValue,
			);

			setExperimentValue((prevValue) => {
				if (prevValue !== experimentValue) {
					// We only want to detect when a user should see the experiment, but it didn't because of the async admin check
					if (experimentValue === true) {
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								source: 'confluence-frontend',
								action: 'changed',
								actionSubject: 'eligibilityCriteria',
								attributes: {
									experiment: 'aiUpsellInEditorFree',
								},
							},
						}).fire();
					}

					return experimentValue;
				}
				return prevValue;
			});
			setIsLoading(isAdminCheckLoading);
		}
	}, [
		createAnalyticsEvent,
		isAdminCheckLoading,
		isSiteAdmin,
		siteMeetsRequirements,
		statsigExperimentValue,
	]);

	/**
	 * Will fire the exposure event when the experimentValue is a boolean, to consider CONTROL and VARIATION
	 * @param experimentValue
	 */
	const fireExperimentExposure = useCallback(() => {
		FeatureGates.manuallyLogExperimentExposure('confluence_free_ai_feature_gate');
	}, []);

	const triggerUpsellFlow = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'aiUpsellInEditorFree',
				action: 'triggerUpsellFlow',
				actionSubject: 'aiInEditorUpsellModal',
				actionSubjectId: 'aiInEditorUpsellTrigger',
				attributes: {},
			},
		}).fire();

		setShowUpsellModal(true);
	}, [setShowUpsellModal, createAnalyticsEvent]);

	const UpsellModal = useCallback(() => {
		const onCloseModal = () => setShowUpsellModal(false);

		return showUpsellModal ? LoadableConfluenceAiUpsellModal({ onCloseModal }) : null;
	}, [showUpsellModal]);

	return {
		isLoading,
		isEnrolledInExperiment,
		isExperimentEnabled,
		fireExperimentExposure,
		triggerUpsellFlow,
		UpsellModal,
	};
};

export const AIUpsellButtonWrapper = ({ children }: PropsWithChildren<{}>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		fireEventOnce(
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluence-frontend',
					action: 'viewed',
					actionSubject: 'button',
					actionSubjectId: 'aiUpsellInEditorFreeToolbarButton',
					attributes: {},
				},
			}).fire,
		);
	}, [createAnalyticsEvent]);

	return (
		<div css={aiUpsellButtonContainerStyles} data-testid="ai-upsell-button-wrapper">
			<div css={aiUpsellButtonInnerStyles}>{children}</div>
		</div>
	);
};
